'use client';

import Image from 'next/image';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function YuzDolgusuSonrasiAlkolAlimininSonuclari() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/yuz-dolgusu-sonrasi-alkol-aliminin-sonuclari';
  const pageTitle = 'Yüz Dolgusu Sonrası Alkol Alımının Sonuçları';
  const pageDescription = 'Yüz dolgu uygulamasından sonra alkol içmek neden sakıncalıdır? Morarma, ödem, dolgu yer değişimi ve doku hassasiyeti üzerindeki etkiler — bilimsel açıklamalar ve klinik önerilerle.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-violet-50 to-purple-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Yüz Dolgusu Sonrası Alkol Alımının Sonuçları', href: '/tr/blog/yuz-dolgusu-sonrasi-alkol-aliminin-sonuclari' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Yüz dolgusu sonrası alkol alımının sonuçları"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-violet-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Yüz Dolgusu
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Yüz Dolgusu Sonrası Alkol Alımının Sonuçları
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>4 Mart 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>7 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Yüz dolgusu, yaşla birlikte azalan hacmi geri kazandırmak, yüz hatlarını belirginleştirmek ve genç, dengeli bir görünüm sağlamak için yapılan en popüler medikal estetik işlemlerden biridir.
                Ancak bu işlemin başarısı yalnızca kullanılan ürünün kalitesiyle değil, işlem sonrası dönemde gösterilen özenle de yakından ilgilidir.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                En sık sorulan sorulardan biri:
              </p>

              <div className="bg-violet-50 border-l-4 border-violet-400 p-4 mb-6">
                <p className="text-violet-800 font-medium text-lg">
                  "Dolgu yaptırdım, akşam arkadaşlarla bir iki kadeh içsem olur mu?"
                </p>
              </div>

              <div className="bg-red-50 border-l-4 border-red-400 p-4 mb-6">
                <p className="text-red-800 font-medium">
                  Kısa cevabı net: Hayır, önerilmez.
                </p>
                <p className="text-red-800">
                  Çünkü alkol, damarlarda ve dokularda öyle bir zincir reaksiyon başlatır ki, dolgunun yerleşimini doğrudan etkileyebilir.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣ Yüz Dolgusu Nedir ve Nasıl Çalışır?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Yüz dolgularının büyük kısmı hyaluronik asit (HA) içeriklidir.
                Bu madde, cilt altındaki suyu bağlayarak hacim kazandırır ve cilt elastikiyetini destekler.
              </p>

              <p className="text-gray-700 mb-4 leading-relaxed">
                İşlem sonrası ilk 48 saat, dolgu maddesinin dokuya uyum sağladığı, mikroskobik damarların yeniden düzenlendiği en kritik dönemdir.
                Bu dönemde dolaşımı etkileyen her şey (örneğin alkol), dolgu davranışını değiştirebilir.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2️⃣ Alkolün Fizyolojik Etkisi
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-violet-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Etki</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Mekanizma</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Sonuç</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Vazodilatasyon (damar genişlemesi)</td>
                      <td className="border border-gray-300 px-4 py-3">Damar çapı artar, cilt altına fazla kan akışı olur</td>
                      <td className="border border-gray-300 px-4 py-3">Morarma riski artar</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kan incelmesi</td>
                      <td className="border border-gray-300 px-4 py-3">Pıhtılaşma gecikir</td>
                      <td className="border border-gray-300 px-4 py-3">Küçük kılcal sızıntılar oluşabilir</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sıvı tutulumu (ödem)</td>
                      <td className="border border-gray-300 px-4 py-3">Hücre dışı sıvı birikir</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgu hacmi geçici olarak artar</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Hyaluronik asit davranışı değişir</td>
                      <td className="border border-gray-300 px-4 py-3">Su tutma dengesi bozulur</td>
                      <td className="border border-gray-300 px-4 py-3">Şişlik, asimetri riski</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Karaciğer yükü</td>
                      <td className="border border-gray-300 px-4 py-3">HA metabolizması yavaşlar</td>
                      <td className="border border-gray-300 px-4 py-3">Etki oturması gecikir</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div className="bg-orange-50 border-l-4 border-orange-400 p-4 mb-6">
                <p className="text-orange-800 font-medium">
                  📍 Özetle: Alkol, dolgunun dokuda "yerleşme" sürecini hem fiziksel hem biyokimyasal olarak zorlaştırır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3️⃣ Neden 48 Saatlik Alkol Yasağı Vardır?
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Dolgu işlemi sonrası dokuda:
              </p>

              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-blue-800">
                  <li>Kılcal damar geçirgenliği artar,</li>
                  <li>Lenfatik drenaj yavaşlar,</li>
                  <li>Doku içi sıvı dengesi değişir.</li>
                </ul>
              </div>

              <p className="text-gray-700 mb-4 leading-relaxed">
                Bu nedenle ilk 48 saat, dokunun kendi iç dengesini bulma sürecidir.
                Alkol bu dönemde dolaşımı hızlandırdığı için;
              </p>

              <div className="bg-red-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-red-800">
                  <li>Şişlik,</li>
                  <li>Morarma,</li>
                  <li>Dengesiz dolgu dağılımı gibi geçici komplikasyonlar gelişebilir.</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4️⃣ Hangi Bölgelerde Risk Daha Fazla?
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-purple-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Bölge</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Risk Düzeyi</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Açıklama</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Elmacık kemiği</td>
                      <td className="border border-gray-300 px-4 py-3">Orta</td>
                      <td className="border border-gray-300 px-4 py-3">Şişlik ödem riski</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Nazolabial (burun kenarı)</td>
                      <td className="border border-gray-300 px-4 py-3">Yüksek</td>
                      <td className="border border-gray-300 px-4 py-3">Kılcal damar yoğunluğu fazla</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Göz altı (ışık dolgusu)</td>
                      <td className="border border-gray-300 px-4 py-3">Yüksek</td>
                      <td className="border border-gray-300 px-4 py-3">Damar geçirgenliği çok hassas</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Çene hattı / Jawline</td>
                      <td className="border border-gray-300 px-4 py-3">Orta</td>
                      <td className="border border-gray-300 px-4 py-3">Su tutulumu asimetri yaratabilir</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5️⃣ Görsel ve Estetik Sonuçlar
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Erken dönemde alkol alınırsa:
              </p>

              <div className="bg-yellow-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-yellow-800">
                  <li>Şişlik nedeniyle dolgunun şekli bozulabilir.</li>
                  <li>Asimetrik görünüm oluşabilir.</li>
                  <li>Doku baskısı, dolgu partiküllerinin yönünü değiştirebilir.</li>
                </ul>
              </div>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bazı durumlarda hasta "dolgum kaydı" veya "yüzüm yamuk oldu" sanabilir; oysa bu durum genellikle reaktif ödemdir ve 2–3 günde geriler.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                6️⃣ Klinik Örnek
              </h2>
              
              <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
                <p className="text-blue-800 italic">
                  "Bir hastam elmacık dolgusu sonrası akşam bira içtiğini söyledi.
                  Ertesi gün tek taraflı şişlik gelişti, göz çevresi hafif morardı.
                  Soğuk kompres ve drenajla 3 günde normale döndü, dolgu yerleşimi etkilenmedi.
                  Ama o süreçte yaşadığı stres tamamen gereksizdi."
                </p>
                <p className="text-blue-800 font-medium mt-4">
                  — Dr. Barış Çin, Estetik Cerrahi Uzmanı
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                7️⃣ Alkolün Dolgu Kalıcılığına Etkisi
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Sürekli alkol kullanan kişilerde vücudun oksidatif stres seviyesi yükselir.
                Bu durum, hyaluronik asit moleküllerinin parçalanma sürecini hızlandırır.
              </p>

              <div className="bg-violet-50 rounded-xl p-6 mb-6">
                <div className="text-center">
                  <p className="text-violet-800 font-medium mb-4">Dolgu Kalıcılık Süresi Karşılaştırması</p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="bg-violet-100 rounded-lg p-4">
                      <p className="text-violet-900 font-semibold">Normal Kullanım</p>
                      <p className="text-violet-800 text-2xl font-bold">12-15 ay</p>
                    </div>
                    <div className="bg-red-100 rounded-lg p-4">
                      <p className="text-red-900 font-semibold">Sık Alkol Kullanımı</p>
                      <p className="text-red-800 text-2xl font-bold">8-10 ay</p>
                    </div>
                  </div>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                8️⃣ Alkol ile Birlikte Kaçınılması Gerekenler
              </h2>
              
              <div className="overflow-x-auto mb-6">
                <table className="w-full border-collapse border border-gray-300">
                  <thead>
                    <tr className="bg-orange-50">
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Faktör</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Etki</th>
                      <th className="border border-gray-300 px-4 py-3 text-left font-semibold text-gray-800">Sonuç</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sigara</td>
                      <td className="border border-gray-300 px-4 py-3">Damar daralması + toksin yükü</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgu homojenliği azalır</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Sıcak ortam (hamam, sauna)</td>
                      <td className="border border-gray-300 px-4 py-3">Damar genişlemesi</td>
                      <td className="border border-gray-300 px-4 py-3">Şişlik riski artar</td>
                    </tr>
                    <tr>
                      <td className="border border-gray-300 px-4 py-3 font-medium">Egzersiz</td>
                      <td className="border border-gray-300 px-4 py-3">Kan basıncı artışı</td>
                      <td className="border border-gray-300 px-4 py-3">Dolgu yayılımı</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="border border-gray-300 px-4 py-3 font-medium">Kan sulandırıcı ilaçlar</td>
                      <td className="border border-gray-300 px-4 py-3">Morarma riski</td>
                      <td className="border border-gray-300 px-4 py-3">Artış gösterir</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                9️⃣ Estetik Psikoloji Perspektifi
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Yüz dolgusu, sadece fiziksel hacmi değil, duygusal ifadeyi de etkiler.
                Alkol sonrası gelişen şişlik veya asimetri, hastada "pişmanlık" ve "güven kaybı" hissi yaratabilir.
                Oysa genellikle geçici bir durumdur.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed">
                Bu nedenle işlem sonrası dönemde sabır ve bilinçli davranış çok değerlidir.
              </p>

              <div className="bg-indigo-50 border-l-4 border-indigo-400 p-4 mb-6">
                <p className="text-indigo-800 font-medium">
                  "Estetikte sabır, sonucun görünmeyen estetiğidir."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                🔟 Sonuçların Yönetimi
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Alkol alınmış ve şişlik gelişmişse:
              </p>

              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside space-y-2 text-green-800">
                  <li>Soğuk kompres (15 dk aralıklarla)</li>
                  <li>Bol su ve elektrolit desteği</li>
                  <li>Yüksek yastıkta uyuma</li>
                  <li>Tuzsuz beslenme</li>
                  <li>Gerekirse antiödem destek krem (doktor önerisiyle)</li>
                </ul>
                <p className="text-green-800 mt-4">
                  3–5 gün içinde doku dengesi genellikle kendiliğinden normale döner.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1️⃣1️⃣ Sık Sorulan Sorular (FAQ)
              </h2>
              
              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">1. Dolgu sonrası alkol içtim, dolgu bozulur mu?</h3>
                  <p className="text-gray-600">Tamamen değil ama simetriyi geçici olarak bozabilir.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">2. Morarma ne kadar sürer?</h3>
                  <p className="text-gray-600">Genellikle 3–5 gün içinde geçer.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">3. Alkol dolgunun ömrünü kısaltır mı?</h3>
                  <p className="text-gray-600">Evet, sık alkol kullanımı hyaluronik asit metabolizmasını hızlandırır.</p>
                </div>
                
                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">4. Ne kadar süre alkol almamalıyım?</h3>
                  <p className="text-gray-600">En az 48 saat, tercihen 72 saat beklenmeli.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">5. Hangi bölgelerde risk daha fazla?</h3>
                  <p className="text-gray-600">Göz altı ve nazolabial bölgelerde risk yüksektir.</p>
                </div>

                <div className="bg-gray-50 p-6 rounded-lg">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">6. Şişlik ne zaman normale döner?</h3>
                  <p className="text-gray-600">Genellikle 2-3 gün içinde azalır, 5 günde tamamen geçer.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sonuç ve Klinik Etik Not
              </h2>
              
              <p className="text-gray-700 mb-4 leading-relaxed">
                Yüz dolgusu sonrası alkol, kısa süreli keyif gibi görünse de estetik sonucu uzun vadede etkileyebilir.
                Basit bir dikkat, günlerce süren ödem ve stresin önüne geçer.
              </p>

              <div className="bg-red-50 border-l-4 border-red-400 p-6 mb-6">
                <p className="text-red-800 font-medium">
                  🩺 Dr. Barış Çin öneriyor:
                </p>
                <p className="text-red-800 mt-2">
                  "Dolgu sonrası ilk 48 saat, güzelliğin korunma dönemidir.
                  Vücudunu alkolden koru ki, dolgu seni en güzel haline taşısın."
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              
              <div className="bg-gray-50 rounded-xl p-6 mb-8">
                <ul className="list-disc list-inside space-y-2 text-gray-700">
                  <li>ISAPS (2024) – "Post-Dermal Filler Care Guidelines"</li>
                  <li>Teoxane Clinical Data (2023) – "Hyaluronic Acid Hydrodynamics"</li>
                  <li>Harvard Health (2024) – "Ethanol Effects on Microcirculation"</li>
                  <li>Türk Estetik Tıp Derneği (TETD) – "Dolgu Sonrası Komplikasyon Yönetimi"</li>
                </ul>
              </div>

              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-violet-100 text-violet-800 px-3 py-1 rounded-full text-sm">#yüzdolgu</span>
                <span className="bg-violet-100 text-violet-800 px-3 py-1 rounded-full text-sm">#alkol</span>
                <span className="bg-violet-100 text-violet-800 px-3 py-1 rounded-full text-sm">#ödem</span>
                <span className="bg-violet-100 text-violet-800 px-3 py-1 rounded-full text-sm">#morarma</span>
                <span className="bg-violet-100 text-violet-800 px-3 py-1 rounded-full text-sm">#estetik</span>
                <span className="bg-violet-100 text-violet-800 px-3 py-1 rounded-full text-sm">#DrBarışÇin</span>
              </div>

              {/* Social Share Buttons */}
              <div className="border-t pt-8 mt-8">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Bu yazıyı paylaş:</h3>
                <div className="flex space-x-4">
                  <button
                    onClick={() => handleShare('facebook')}
                    className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <FaFacebookF className="w-4 h-4" />
                    <span>Facebook</span>
                  </button>
                  <button
                    onClick={() => handleShare('twitter')}
                    className="flex items-center space-x-2 bg-blue-400 text-white px-4 py-2 rounded-lg hover:bg-blue-500 transition-colors"
                  >
                    <FaTwitter className="w-4 h-4" />
                    <span>Twitter</span>
                  </button>
                  <button
                    onClick={() => handleShare('linkedin')}
                    className="flex items-center space-x-2 bg-blue-700 text-white px-4 py-2 rounded-lg hover:bg-blue-800 transition-colors"
                  >
                    <FaLinkedinIn className="w-4 h-4" />
                    <span>LinkedIn</span>
                  </button>
                  <button
                    onClick={() => handleShare('whatsapp')}
                    className="flex items-center space-x-2 bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors"
                  >
                    <FaWhatsapp className="w-4 h-4" />
                    <span>WhatsApp</span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </article>

        {/* CTA Section */}
        <div className="mt-12 bg-gradient-to-r from-violet-600 to-purple-700 rounded-3xl p-8 text-white text-center">
          <h3 className="text-2xl font-bold mb-4">Yüz Dolgusu Uygulaması ve Bakım Danışmanlığı</h3>
          <p className="mb-6">Uzman doktorumuzdan yüz dolgusu uygulaması ve sonrası bakım hakkında detaylı bilgi alın.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href={`https://wa.me/905438999586?text=${encodeURIComponent('Yüz dolgusu uygulaması ve sonrası bakım hakkında bilgi almak istiyorum.')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
            >
              <FaWhatsapp className="w-5 h-5 mr-2" />
              WhatsApp ile İletişim
            </a>
            <a
              href="/tr/iletisim"
              className="inline-flex items-center justify-center px-6 py-3 bg-white text-violet-600 rounded-full hover:bg-gray-100 transition-colors"
            >
              Randevu Al
            </a>
          </div>
        </div>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <a
              href="/tr/blog/dudak-dolgusu-sonrasi-alkol-tuketimi-bilimsel-etkiler-klinik-bulgular-ve-estetik-sonuclar"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dudak Dolgusu Sonrası Alkol Tüketimi</h4>
              <p className="text-gray-600 text-sm">Dudak dolgusu sonrası alkol neden önerilmez? Hyaluronik asit dengesi ve damar geçirgenliği.</p>
            </a>
            <a
              href="/tr/blog/botoks-sonrasi-alkol-tuketimi-bilimsel-aciklamalar-klinik-etkiler-ve-uzman-onerileri"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Botoks Sonrası Alkol Tüketimi</h4>
              <p className="text-gray-600 text-sm">Botoks sonrası alkol içmek neden önerilmez? Dolaşım, kas aktivitesi ve ödem üzerindeki biyolojik etkiler.</p>
            </a>
            <a
              href="/tr/blog/sivi-yuz-germe-nedir-hangi-icerikli-urunler-kullanilir"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Sıvı Yüz Germe Nedir?</h4>
              <p className="text-gray-600 text-sm">Sıvı yüz germe nedir, hangi içerikli ürünler kullanılır? Hacim artırıcı dolgular ve uygulama teknikleri.</p>
            </a>
            <div className="bg-white rounded-2xl shadow-lg p-6 text-center">
              <p className="text-gray-600 mb-4">
                Daha fazla bilgilendirici içerik için bizi takip edin.
              </p>
              <a
                href="/tr/blog"
                className="inline-flex items-center px-4 py-2 bg-violet-600 text-white rounded-full hover:bg-violet-700 transition-colors text-sm"
              >
                Tüm Blog Yazılarını Görüntüle
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
